import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom';
import { I_S3Credentials } from '../../../interfaces/S3Types';
import routes from '../../../Resources/routes.json';
import CircularProgress from '@mui/material/CircularProgress';
import { JSONRecordData } from '../Log/graph.util';
import Button from '@mui/material/Button';
import { FormattedMessage } from 'react-intl';
import MainStatsSalidomo from './MainStatsSalidomo';
import DetailedBatteryViewSalidomo from './DetailedBatteryViewSalidomo';

interface BatteryViewProps {
  values: JSONRecordData;
  s3Credentials: I_S3Credentials;
  installationId: number;
  productNum: number;
  connected: boolean;
}

function BatteryViewSalidomo(props: BatteryViewProps) {
  if (props.values === null && props.connected == true) {
    return null;
  }

  const currentLocation = useLocation();
  const navigate = useNavigate();

  const sortedBatteryView =
    props.values != null
      ? Object.entries(props.values.Battery.Devices)
          .map(([BatteryId, battery]) => {
            return { BatteryId, battery }; // Here we return an object with the id and device
          })
          .sort((a, b) => parseInt(b.BatteryId) - parseInt(a.BatteryId))
      : [];

  const [loading, setLoading] = useState(sortedBatteryView.length == 0);

  const handleMainStatsButton = () => {
    navigate(routes.mainstats);
  };

  useEffect(() => {
    if (sortedBatteryView.length == 0) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [sortedBatteryView]);

  return (
    <>
      {!props.connected && (
        <Container
          maxWidth="xl"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '70vh'
          }}
        >
          <CircularProgress size={60} style={{ color: '#ffc04d' }} />
          <Typography
            variant="body2"
            style={{ color: 'black', fontWeight: 'bold' }}
            mt={2}
          >
            Unable to communicate with the installation
          </Typography>
          <Typography variant="body2" style={{ color: 'black' }}>
            Please wait or refresh the page
          </Typography>
        </Container>
      )}
      {loading && props.connected && (
        <Container
          maxWidth="xl"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '70vh'
          }}
        >
          <CircularProgress size={60} style={{ color: '#ffc04d' }} />
          <Typography
            variant="body2"
            style={{ color: 'black', fontWeight: 'bold' }}
            mt={2}
          >
            Battery service is not available at the moment
          </Typography>
          <Typography variant="body2" style={{ color: 'black' }}>
            Please wait or refresh the page
          </Typography>
        </Container>
      )}

      {!loading && props.connected && (
        <Container maxWidth="xl">
          <Grid container>
            <Grid
              item
              xs={6}
              md={6}
              sx={{
                display:
                  !currentLocation.pathname.includes('detailed_view') &&
                  !currentLocation.pathname.includes('mainstats')
                    ? 'block'
                    : 'none'
              }}
            >
              <Button
                variant="contained"
                sx={{
                  marginTop: '20px',
                  backgroundColor: '#808080',
                  color: '#000000',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
              >
                <FormattedMessage
                  id="battery_view"
                  defaultMessage="Battery View"
                />
              </Button>

              <Button
                variant="contained"
                onClick={handleMainStatsButton}
                sx={{
                  marginTop: '20px',
                  marginLeft: '20px',
                  backgroundColor: '#ffc04d',
                  color: '#000000',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
              >
                <FormattedMessage id="main_stats" defaultMessage="Main Stats" />
              </Button>
            </Grid>
          </Grid>

          <Grid container>
            <Routes>
              <Route
                path={routes.mainstats + '/*'}
                element={
                  <MainStatsSalidomo
                    s3Credentials={props.s3Credentials}
                    id={props.installationId}
                  ></MainStatsSalidomo>
                }
              />
              {Object.entries(props.values.Battery.Devices).map(
                ([BatteryId, battery]) => (
                  <Route
                    key={routes.detailed_view + BatteryId}
                    path={routes.detailed_view + BatteryId}
                    element={
                      <DetailedBatteryViewSalidomo
                        batteryId={Number(BatteryId)}
                        s3Credentials={props.s3Credentials}
                        batteryData={battery}
                        installationId={props.installationId}
                        productNum={props.productNum}
                      ></DetailedBatteryViewSalidomo>
                    }
                  />
                )
              )}
            </Routes>
          </Grid>

          <TableContainer
            component={Paper}
            sx={{
              marginTop: '20px',
              marginBottom: '20px',
              display:
                !currentLocation.pathname.includes('detailed_view') &&
                !currentLocation.pathname.includes('mainstats')
                  ? 'block'
                  : 'none'
            }}
          >
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="center">Battery</TableCell>
                  <TableCell align="center">Firmware</TableCell>
                  <TableCell align="center">Power</TableCell>
                  <TableCell align="center">Voltage</TableCell>
                  <TableCell align="center">SoC</TableCell>
                  <TableCell align="center">Temperature</TableCell>
                  <TableCell align="center">Warnings</TableCell>
                  <TableCell align="center">Alarms</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedBatteryView.map(({ BatteryId, battery }) => (
                  <TableRow
                    key={BatteryId}
                    style={{
                      height: '10px'
                    }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      align="center"
                      sx={{ fontWeight: 'bold' }}
                    >
                      <Link
                        style={{ color: 'black' }}
                        to={routes.detailed_view + BatteryId}
                      >
                        {'Node ' + BatteryId}
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '10%',
                        textAlign: 'center'
                      }}
                    >
                      {battery.FwVersion}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '10%',
                        textAlign: 'center'
                      }}
                    >
                      {battery.Dc.Power + ' W'}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '10%',
                        textAlign: 'center',

                        backgroundColor:
                          battery.Dc.Voltage < 44 || battery.Dc.Voltage > 57
                            ? '#FF033E'
                            : '#32CD32',
                        color: battery.Dc.Voltage ? 'inherit' : 'white'
                      }}
                    >
                      {battery.Dc.Voltage + ' V'}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '10%',
                        textAlign: 'center',
                        backgroundColor:
                          battery.Soc < 20
                            ? '#FF033E'
                            : battery.Soc < 50
                            ? '#ffbf00'
                            : '#32CD32',
                        color: battery.Soc ? 'inherit' : 'white'
                      }}
                    >
                      {battery.Soc + ' %'}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '10%',
                        textAlign: 'center',
                        backgroundColor:
                          battery.Temperatures.Cells.Average > 300
                            ? '#FF033E'
                            : battery.Temperatures.Cells.Average > 280
                            ? '#ffbf00'
                            : battery.Temperatures.Cells.Average < 245
                            ? '#008FFB'
                            : '#32CD32'
                      }}
                    >
                      {battery.Temperatures.Cells.Average + ' C'}
                    </TableCell>

                    {/*{props.productNum === 0 && (*/}
                    {/*  <>*/}
                    {/*    <TableCell*/}
                    {/*      style={{*/}
                    {/*        width: '20%',*/}
                    {/*        textAlign: 'center',*/}
                    {/*        padding: '8px',*/}
                    {/*        fontWeight:*/}
                    {/*          battery.Warnings.value !== ''*/}
                    {/*            ? 'bold'*/}
                    {/*            : 'inherit',*/}
                    {/*        backgroundColor:*/}
                    {/*          battery.Warnings.value === ''*/}
                    {/*            ? 'inherit'*/}
                    {/*            : '#ff9900',*/}
                    {/*        color:*/}
                    {/*          battery.Warnings.value != '' ? 'black' : 'inherit'*/}
                    {/*      }}*/}
                    {/*    >*/}
                    {/*      {battery.Warnings.value === '' ? (*/}
                    {/*        'None'*/}
                    {/*      ) : battery.Warnings.value.toString().split('-')*/}
                    {/*          .length > 1 ? (*/}
                    {/*        <Link*/}
                    {/*          style={{ color: 'black' }}*/}
                    {/*          to={*/}
                    {/*            currentLocation.pathname.substring(*/}
                    {/*              0,*/}
                    {/*              currentLocation.pathname.lastIndexOf('/') + 1*/}
                    {/*            ) +*/}
                    {/*            routes.log +*/}
                    {/*            '?open=warning'*/}
                    {/*          }*/}
                    {/*        >*/}
                    {/*          Multiple Warnings*/}
                    {/*        </Link>*/}
                    {/*      ) : (*/}
                    {/*        battery.Warnings.value*/}
                    {/*      )}*/}
                    {/*    </TableCell>*/}
                    {/*    <TableCell*/}
                    {/*      sx={{*/}
                    {/*        width: '20%',*/}
                    {/*        textAlign: 'center',*/}
                    {/*        fontWeight:*/}
                    {/*          battery.Alarms.value !== '' ? 'bold' : 'inherit',*/}
                    {/*        backgroundColor:*/}
                    {/*          battery.Alarms.value === ''*/}
                    {/*            ? 'inherit'*/}
                    {/*            : '#FF033E',*/}
                    {/*        color:*/}
                    {/*          battery.Alarms.value != '' ? 'black' : 'inherit'*/}
                    {/*      }}*/}
                    {/*    >*/}
                    {/*      {battery.Alarms.value === '' ? (*/}
                    {/*        'None'*/}
                    {/*      ) : battery.Alarms.value.toString().split('-')*/}
                    {/*          .length > 1 ? (*/}
                    {/*        <Link*/}
                    {/*          style={{ color: 'black' }}*/}
                    {/*          to={*/}
                    {/*            currentLocation.pathname.substring(*/}
                    {/*              0,*/}
                    {/*              currentLocation.pathname.lastIndexOf('/') + 1*/}
                    {/*            ) +*/}
                    {/*            routes.log +*/}
                    {/*            '?open=error'*/}
                    {/*          }*/}
                    {/*        >*/}
                    {/*          Multiple Alarms*/}
                    {/*        </Link>*/}
                    {/*      ) : (*/}
                    {/*        battery.Alarms.value*/}
                    {/*      )}*/}
                    {/*    </TableCell>*/}
                    {/*  </>*/}
                    {/*)}*/}

                    {props.productNum === 1 && (
                      <>
                        <TableCell
                          style={{
                            width: '20%',
                            textAlign: 'center',
                            padding: '8px',
                            fontWeight:
                              Number(battery.Warnings) !== 0
                                ? 'bold'
                                : 'inherit',
                            backgroundColor:
                              Number(battery.Warnings) === 0
                                ? 'inherit'
                                : '#ff9900',
                            color:
                              Number(battery.Warnings) != 0
                                ? 'black'
                                : 'inherit'
                          }}
                        >
                          {Number(battery.Warnings) === 0 ? (
                            'None'
                          ) : Number(battery.Warnings) === 1 ? (
                            <Link
                              style={{ color: 'black' }}
                              to={
                                currentLocation.pathname.substring(
                                  0,
                                  currentLocation.pathname.lastIndexOf('/') + 1
                                ) +
                                routes.log +
                                '?open=warning'
                              }
                            >
                              New Warning
                            </Link>
                          ) : (
                            <Link
                              style={{ color: 'black' }}
                              to={
                                currentLocation.pathname.substring(
                                  0,
                                  currentLocation.pathname.lastIndexOf('/') + 1
                                ) +
                                routes.log +
                                '?open=warning'
                              }
                            >
                              Multiple Warnings
                            </Link>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            width: '20%',
                            textAlign: 'center',
                            fontWeight:
                              Number(battery.Alarms) !== 0 ? 'bold' : 'inherit',
                            backgroundColor:
                              Number(battery.Alarms) === 0
                                ? 'inherit'
                                : '#FF033E',
                            color:
                              Number(battery.Alarms) != 0 ? 'black' : 'inherit'
                          }}
                        >
                          {Number(battery.Alarms) === 0 ? (
                            'None'
                          ) : Number(battery.Alarms) === 1 ? (
                            <Link
                              style={{ color: 'black' }}
                              to={
                                currentLocation.pathname.substring(
                                  0,
                                  currentLocation.pathname.lastIndexOf('/') + 1
                                ) +
                                routes.log +
                                '?open=error'
                              }
                            >
                              New Alarm
                            </Link>
                          ) : (
                            <Link
                              style={{ color: 'black' }}
                              to={
                                currentLocation.pathname.substring(
                                  0,
                                  currentLocation.pathname.lastIndexOf('/') + 1
                                ) +
                                routes.log +
                                '?open=error'
                              }
                            >
                              Multiple Alarms
                            </Link>
                          )}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      )}
    </>
  );
}

export default BatteryViewSalidomo;
